import React, {useEffect} from 'react';
import {Route, Switch, useHistory} from 'react-router-dom';
import {LoginCallback, SecureRoute, Security} from '@okta/okta-react';
import {TimeOffTable} from './components/TimeOff';
import {Employees} from './components/Employees';
import {NoPermission} from './components/NoPermission';
import {oktaAuthConfig} from './config';
import {Header} from './components/Header';
import {Footer} from './components/Footer';
import {Layout, notification} from 'antd';
import {OktaAuth, toRelativeUrl} from '@okta/okta-auth-js';
import {useUserState} from './auth/userHooks';
import UserContext from './auth/userContext';
import {PATHS} from './components/Constants';
import {Exports} from './components/d365/Exports';
import {Uploads} from './components/d365/Uploads';
import {Expenses} from './components/d365/Expenses';
import {ExpenseTypes} from './components/d365/ExpenseTypes';
import {ExpenseCodes} from './components/d365/ExpenseCodes';
import {ExpenseMappings} from './components/d365/ExpenseMappings';
import {Hours} from './components/d365/Hours';
import {Rates} from './components/d365/Rates';
import {Workers} from './components/d365/Workers';
import {UnfoundProjects} from './components/d365/UnfoundProjects';
import {HourLogPeriods} from './components/d365/HourLogPeriods';
import {ProjectSearch} from './components/d365/ProjectSearch';
import {LiftoffUsers} from './components/liftoff-users/LiftoffUsers';
import {LiftoffMapping} from './components/liftoff-mapping/LiftoffMapping';
import {LiftoffMappingGroups} from './components/liftoff-mapping/mappings';
import {ExpenseFiles} from './components/d365/ExpenseFiles';
import {Payments} from './components/d365/Payments';
import {LeaveOfAbsenceTable} from './components/leave_of_absence/LeaveOfAbsence';
import Login from './components/Login';
import {getPrincipal} from './api/principalApi';
import {setAccessToken} from './auth/token';
import {Scheduled} from './components/Scheduled';
import {EmployeesBulk} from "./components/EmployeesBulk";

const oktaAuth = new OktaAuth(oktaAuthConfig);

const AppWithRouterAccess = () => {
  const [ user, setUser ] = useUserState();
  const history = useHistory();

  const authenticate = async () => {
    await oktaAuth.signInWithRedirect();
  };

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    window.location.reload();
  };

  useEffect(() => {
    oktaAuth.isAuthenticated().then((authenticated) => {
      if (authenticated) {
        setAccessToken(oktaAuth.getAccessToken());
        getPrincipal().then((principal) => {
          if (principal) {
            setUser(principal);
          } else {
            notification.error({
              message: `Error connecting to server`,
              description: 'Contact na_opsenablement@credera.com if problem persists'
            });
          }
        });
      }
    });
    // eslint-disable-next-line
  }, []);

  return (
    <Security
      oktaAuth={oktaAuth}
      onAuthRequired={authenticate}
      restoreOriginalUri={restoreOriginalUri}
    >
      <UserContext.Provider value={{ user }}>
        <Layout style={{ minHeight: '100vh' }}>
          <Header/>
          <Layout.Content
            className="site-layout"
            style={{ padding: '0 50px', marginTop: 64 }}
          >
            <Switch>
              <SecureRoute path="/" exact={true}>
                {/*This is to push to display something secure so we go to /login if we are not authenticated,*/}
                {/*header will make sure to display correct Route */}
              </SecureRoute>
              <SecureRoute
                path={PATHS.EMPLOYEES}
                exact={true}
                component={Employees}
              />
              <SecureRoute
                path={PATHS.GET_EMPLOYEES_BULK}
                exact={true}
                component={EmployeesBulk}
              />
              {/* @TODO REMOVE <SecureRoute*/}
              {/*  path={PATHS.DOWNLOAD_EMPLOYEE_BULK_TEMPLATE}*/}
              {/*  exact={true}*/}
              {/*  component={EmployeesBulk}*/}
              {/*/>*/}
              {/*<SecureRoute*/}
              {/*  path={PATHS.SUBMIT_EMPLOYEES_BULK}*/}
              {/*  exact={true}*/}
              {/*  component={EmployeesBulk}*/}
              {/*/>*/}
              <SecureRoute
                path={PATHS.SCHEDULED}
                exact={true}
                component={Scheduled}
              />
              <SecureRoute
                path={PATHS.PTO}
                exact={true}
                component={TimeOffTable}
              />
              <SecureRoute
                path={PATHS.LOA}
                exact={true}
                component={LeaveOfAbsenceTable}
              />
              <SecureRoute
                path={PATHS.D365_EXPORTS}
                exact={true}
                component={Exports}
              />
              <SecureRoute
                path={PATHS.D365_ADJUSTMENTS_UPLOADS}
                exact={true}
                component={Uploads}
              />
              {/*<SecureRoute*/}
              {/*  path={PATHS.D365_ADJUSTMENTS_ALL}*/}
              {/*  exact={true}*/}
              {/*  component={Adjustments}*/}
              {/*/>*/}
              <SecureRoute
                path={PATHS.D365_HOUR_LOG_PERIODS}
                exact={true}
                component={HourLogPeriods}
              />
              <SecureRoute
                path={PATHS.D365_EXPENSES_PROCESSED}
                exact={true}
                component={Expenses}
              />
              <SecureRoute
                path={PATHS.D365_EXPENSES_PROJECTOR_EXPENSE_TYPES}
                exact={true}
                component={ExpenseTypes}
              />
              <SecureRoute
                path={PATHS.D365_EXPENSES_CODES}
                exact={true}
                component={ExpenseCodes}
              />
              <SecureRoute
                path={PATHS.D365_EXPENSES_MAPPINGS}
                exact={true}
                component={ExpenseMappings}
              />
              <SecureRoute
                path={PATHS.D365_EXPENSES_FILES}
                exact={true}
                component={ExpenseFiles}
              />
              <SecureRoute
                path={PATHS.D365_EXPENSES_UNFOUND_PROJECTS}
                exact={true}
                component={UnfoundProjects}
              />
              <SecureRoute
                path={PATHS.D365_HOURS}
                exact={true}
                component={Hours}
              />
              <SecureRoute
                path={PATHS.D365_RATES}
                exact={true}
                component={Rates}
              />
              <SecureRoute
                path={PATHS.D365_WORKERS}
                exact={true}
                component={Workers}
              />
              <SecureRoute
                path={PATHS.D365_PROJECT_SEARCH}
                exact={true}
                component={ProjectSearch}
              />
              <SecureRoute
                path={PATHS.D365_PAYMENTS}
                exact={true}
                component={Payments}
              />
              {LiftoffMappingGroups.map(group =>
                group.mappings.map(m =>
                  <SecureRoute
                    key={m.entity}
                    path={`${PATHS.LIFTOFF_MAPPING}/${group.group}/${m.entity}`}
                    exact={true}
                    component={LiftoffMapping}
                  />
                ))}
              <SecureRoute
                path={PATHS.LIFTOFF_USERS}
                exact={true}
                component={LiftoffUsers}
              />
              <SecureRoute
                path={PATHS.NOPERMISSION}
                exact={true}
                component={NoPermission}
              />
              <Route
                path="/login"
                exact={true}
                render={() => <Login/>}
              />
              <Route path="/login/callback" component={LoginCallback}/>
            </Switch>
          </Layout.Content>
          <Footer/>
        </Layout>
      </UserContext.Provider>
    </Security>
  );
};
export default AppWithRouterAccess;
